import { defineMessages } from "../define";

/**
 * Developer page "Evaluation": production thresholds and switches of the pizza evaluation, with the
 * histogram of each thresholded metric. Setting / group / metric keys of the backend are looked up as
 * `evalSettings.key.<key>.*`, `evalSettings.group.<group>.*` and `evalSettings.metric.<metric>`; unknown
 * keys are shown raw.
 */
export default defineMessages({
  en: {
    "evalSettings.title": "Evaluation",
    "evalSettings.description":
      "Thresholds and switches of the pizza evaluation. A saved change applies to new evaluations on every backend instance within about 10 s; pizzas already evaluated keep their verdict.",
    "evalSettings.forbidden": "Evaluation settings are available to the developer role only.",
    "evalSettings.version": "Version {version}",
    "evalSettings.neverChanged": "Never changed: every value is the deployment default.",
    "evalSettings.lastChange": "Last change by {who}",
    "evalSettings.unknownUser": "unknown user",
    "evalSettings.overriddenCount": "Changed from default: {count}",
    "evalSettings.reload": "Reload",
    "evalSettings.loadFailed": "Could not load the settings: {reason}",
    "evalSettings.error.unknown": "Unknown error",

    // histogram data controls
    "evalSettings.data.label": "Histograms",
    "evalSettings.data.window": "Data window",
    "evalSettings.data.days.1": "24 h",
    "evalSettings.data.days.7": "7 d",
    "evalSettings.data.days.30": "30 d",
    "evalSettings.data.long.1": "Last 24 hours",
    "evalSettings.data.long.7": "Last 7 days",
    "evalSettings.data.long.30": "Last 30 days",
    "evalSettings.data.branch": "Branch",
    "evalSettings.data.allBranches": "All branches",
    "evalSettings.data.since": "since {date}",
    "evalSettings.data.failed": "Histograms could not be loaded: {reason}",

    // groups
    "evalSettings.group.photo_quality": "Photo quality",
    "evalSettings.group.photo_quality.description":
      "A photo that fails one of these checks is marked Invalid photo and the pizza is not evaluated further.",
    "evalSettings.group.photo_quality.fires": "Invalid photo",
    "evalSettings.group.doneness": "Doneness",
    "evalSettings.group.doneness.description":
      "Probabilities of the crust model, computed for valid photos only. From the threshold up the pizza is NOK.",
    "evalSettings.group.doneness.fires": "NOK",
    "evalSettings.group.pizza_saver": "Pizza saver",
    "evalSettings.group.pizza_saver.description": "When a missing pizza saver makes the pizza NOK.",
    "evalSettings.group.pizza_saver.fires": "On a plate",
    "evalSettings.group.extra_ingredients": "Extra ingredients",
    "evalSettings.group.extra_ingredients.description":
      "What the pizza shows beyond its recipe and the customer's modifiers (a swapped pizza, something the kitchen added). Fails the ingredients check: the pizza is NOK. Per ingredient (pieces, sauce share) and per pizza (look-alikes) in the Catalog.",
    "evalSettings.group.extra_ingredients.fires": "Extra, NOK",
    "evalSettings.group.fires": "Over the threshold",

    // settings
    "evalSettings.key.blur_threshold.label": "Blur",
    "evalSettings.key.blur_threshold.description":
      "Probability from the blur model that the photo is blurred. From the threshold up the photo is Invalid photo (pizza blurred).",
    "evalSettings.key.blur_enforce.label": "Enforce the blur check",
    "evalSettings.key.obstacle_coverage_threshold.label": "Obstacle coverage",
    "evalSettings.key.obstacle_coverage_threshold.description":
      "Share of the pizza covered by a hand, paper or a lid (obstacle segmentation). From the threshold up the photo is Invalid photo.",
    "evalSettings.key.obstacle_enforce.label": "Enforce the obstacle check",
    "evalSettings.key.crust_gap_threshold.label": "Crust gap",
    "evalSettings.key.crust_gap_threshold.description":
      "Largest gap in the detected crust ring, as a share of its circumference. Above the threshold the photo is Invalid photo (obstacle).",
    "evalSettings.key.frame_overshoot_threshold.label": "Frame edge",
    "evalSettings.key.frame_overshoot_threshold.description":
      "How far the pizza circle reaches past the edge of the frame, as a share of its radius. Above the threshold the photo is Invalid photo (not the whole pizza).",
    "evalSettings.key.burnt_threshold.label": "Burnt",
    "evalSettings.key.burnt_threshold.description":
      "p(burnt) from the crust model. From the threshold up the pizza is NOK (burnt).",
    "evalSettings.key.raw_threshold.label": "Uncooked",
    "evalSettings.key.raw_threshold.description":
      "p(uncooked) from the crust model. From the threshold up the pizza is NOK (uncooked).",
    "evalSettings.key.plate_waives_saver.label": "Pizza on a plate needs no pizza saver",
    "evalSettings.key.plate_waives_saver.description":
      "On: when the obstacle model finds the pizza on a plate, a missing pizza saver is not a failure. Off: the pizza saver is always required.",
    "evalSettings.key.plate_min_share.label": "Plate share",
    "evalSettings.key.plate_min_share.description":
      "Share of the pizza's surroundings the obstacle model sees as a plate. From the threshold up the pizza counts as on a plate.",
    "evalSettings.key.extra_ingredients_enabled.label": "Check for extra ingredients",
    "evalSettings.key.extra_ingredients_enabled.description":
      "On: ingredients found beyond the order show among the ingredients and make the pizza NOK. Off: not checked.",
    "evalSettings.key.extra_min_confidence.label": "Confidence of a piece",
    "evalSettings.key.extra_min_confidence.description":
      "A detected piece counts only from this confidence up; less certain ones are the detector's usual false finds.",
    "evalSettings.key.extra_min_count.label": "Pieces of an ingredient",
    "evalSettings.key.extra_min_count.description":
      "Confident pieces of one ingredient to count it as extra. A real portion is several pieces, a false find one or two. An ingredient in the Catalog can need fewer (camembert: 2).",

    // metrics (short names in the rule line)
    "evalSettings.metric.blur_p_blurred": "p(blurred)",
    "evalSettings.metric.obstacle_coverage": "covered share",
    "evalSettings.metric.crust_gap_ratio": "crust gap",
    "evalSettings.metric.frame_overshoot_ratio": "overshoot",
    "evalSettings.metric.burnt_probability": "p(burnt)",
    "evalSettings.metric.raw_probability": "p(uncooked)",
    "evalSettings.metric.plate_share": "plate share",
    "evalSettings.metric.plate_share.empty":
      "Plate share is recorded only by evaluations made with obstacle model v7, so the histogram fills up with new evaluations.",

    // a setting card
    "evalSettings.card.overridden": "Changed from default",
    "evalSettings.card.unsaved": "Unsaved",
    "evalSettings.card.pieces": "pcs",
    "evalSettings.card.default": "Default {value}",
    "evalSettings.card.isDefault": "Default value",
    "evalSettings.card.reset": "Reset to default",
    "evalSettings.card.undo": "Undo edit",
    "evalSettings.card.input": "{label} in %",
    "evalSettings.card.saved": "saved {value}",
    "evalSettings.enforce.label": "Enforce",
    "evalSettings.enforce.on": "Enforced",
    "evalSettings.enforce.onHint": "Photos over the threshold fail the check.",
    "evalSettings.enforce.off": "Shadow mode",
    "evalSettings.enforce.offHint": "The threshold only records would_fail; the verdict is not affected.",
    "evalSettings.requires.off": "No effect while “{flag}” is off.",
    "evalSettings.flag.on": "On",
    "evalSettings.flag.off": "Off",

    // histogram
    "evalSettings.chart.title": "Distribution",
    "evalSettings.chart.scale": "Y axis scale",
    "evalSettings.chart.linear": "Lin",
    "evalSettings.chart.log": "Log",
    "evalSettings.chart.linearLong": "Linear count",
    "evalSettings.chart.logLong": "Logarithmic count (shows the tail)",
    "evalSettings.chart.passes": "Passes",
    "evalSettings.chart.shadow": "{outcome} (shadow)",
    "evalSettings.chart.threshold": "Threshold",
    "evalSettings.chart.saved": "Saved",
    "evalSettings.chart.empty": "No values in this window.",
    "evalSettings.chart.noMetric": "This setting has no histogram.",
    "evalSettings.chart.missing": "The backend sent no histogram for {metric}.",
    "evalSettings.chart.share": "{share} of {n}",
    "evalSettings.chart.label": "Histogram of {metric}",

    // numbers under the histogram
    "evalSettings.stats.current": "{outcome} at {rule}",
    "evalSettings.stats.vsSaved": "Change vs saved {rule}",
    "evalSettings.stats.pp": "{value} pp",
    "evalSettings.stats.noChange": "no change",
    "evalSettings.stats.quantiles": "Median · p90 · p99",
    "evalSettings.stats.zero": "Exactly 0: {count} ({share})",
    "evalSettings.stats.versions": "Model versions: {list}",

    // save bar and confirmation
    "evalSettings.save.pending.one": "{count} unsaved change",
    "evalSettings.save.pending.few": "{count} unsaved changes",
    "evalSettings.save.pending.other": "{count} unsaved changes",
    "evalSettings.save.hint": "Applies to new evaluations in production.",
    "evalSettings.save.discard": "Discard",
    "evalSettings.save.review": "Save…",
    "evalSettings.confirm.title": "Save to production?",
    "evalSettings.confirm.description":
      "New evaluations on every backend instance use these values within about 10 s. Pizzas already evaluated are not re-evaluated.",
    "evalSettings.confirm.default": "default",
    "evalSettings.confirm.impact": "{window}: {from} → {to} ({delta})",
    "evalSettings.confirm.save": "Save to production",
    "evalSettings.confirm.invalid": "The backend refused {key}: {error}",
    "evalSettings.confirm.failed": "Saving failed: {error}",
    "evalSettings.toast.saved": "Saved as version {version}. New evaluations use it within about 10 s.",
    "evalSettings.toast.conflict":
      "Someone else saved version {version} in the meantime. The current values were reloaded and your edits kept: review them and save again.",
    "evalSettings.toast.conflictTitle": "Settings changed meanwhile",

    // history
    "evalSettings.history.title": "Change history",
    "evalSettings.history.description": "Saved changes, newest first.",
    "evalSettings.history.empty": "No changes yet: every value is the deployment default.",
    "evalSettings.history.version": "v{version}",
    "evalSettings.history.reset": "back to default",
    "evalSettings.history.showAll": "Show all ({count})",
    "evalSettings.history.showLess": "Show less",
  },
  cs: {
    "evalSettings.title": "Vyhodnocení",
    "evalSettings.description":
      "Prahy a přepínače vyhodnocení pizzy. Uložená změna platí pro nová vyhodnocení na všech instancích backendu zhruba do 10 s; už vyhodnocené pizzy si verdikt ponechají.",
    "evalSettings.forbidden": "Nastavení vyhodnocení je dostupné jen pro roli vývojáře.",
    "evalSettings.version": "Verze {version}",
    "evalSettings.neverChanged": "Zatím beze změn: všechny hodnoty jsou výchozí z nasazení.",
    "evalSettings.lastChange": "Poslední změna: {who}",
    "evalSettings.unknownUser": "neznámý uživatel",
    "evalSettings.overriddenCount": "Mimo výchozí: {count}",
    "evalSettings.reload": "Načíst znovu",
    "evalSettings.loadFailed": "Nastavení se nepodařilo načíst: {reason}",
    "evalSettings.error.unknown": "Neznámá chyba",

    "evalSettings.data.label": "Histogramy",
    "evalSettings.data.window": "Období dat",
    "evalSettings.data.days.1": "24 h",
    "evalSettings.data.days.7": "7 d",
    "evalSettings.data.days.30": "30 d",
    "evalSettings.data.long.1": "Posledních 24 hodin",
    "evalSettings.data.long.7": "Posledních 7 dní",
    "evalSettings.data.long.30": "Posledních 30 dní",
    "evalSettings.data.branch": "Pobočka",
    "evalSettings.data.allBranches": "Všechny pobočky",
    "evalSettings.data.since": "od {date}",
    "evalSettings.data.failed": "Histogramy se nepodařilo načíst: {reason}",

    "evalSettings.group.photo_quality": "Kvalita fotky",
    "evalSettings.group.photo_quality.description":
      "Fotka, která nesplní některou z těchto kontrol, je označena jako neplatná a pizza se dál nevyhodnocuje.",
    "evalSettings.group.photo_quality.fires": "Neplatná fotka",
    "evalSettings.group.doneness": "Propečení",
    "evalSettings.group.doneness.description":
      "Pravděpodobnosti z modelu krusty, počítané jen pro platné fotky. Od prahu výš je pizza NEOK.",
    "evalSettings.group.doneness.fires": "NEOK",
    "evalSettings.group.pizza_saver": "Pizza saver",
    "evalSettings.group.pizza_saver.description": "Kdy chybějící pizza saver znamená NEOK.",
    "evalSettings.group.pizza_saver.fires": "Na talíři",
    "evalSettings.group.extra_ingredients": "Suroviny navíc",
    "evalSettings.group.extra_ingredients.description":
      "Co pizza má nad rámec receptu a úprav zákazníka (záměna pizzy, něco přidaného v kuchyni). Kontrola surovin neprojde: pizza je NEOK. Pro jednotlivé suroviny (kusy, podíl omáčky) a pizzy (podobné suroviny) v Katalogu.",
    "evalSettings.group.extra_ingredients.fires": "Navíc, NEOK",
    "evalSettings.group.fires": "Nad prahem",

    "evalSettings.key.blur_threshold.label": "Rozmazání",
    "evalSettings.key.blur_threshold.description":
      "Pravděpodobnost z modelu rozmazání, že je fotka rozmazaná. Od prahu výš je fotka neplatná (rozmazaná pizza).",
    "evalSettings.key.blur_enforce.label": "Uplatnit kontrolu rozmazání",
    "evalSettings.key.obstacle_coverage_threshold.label": "Zakrytí překážkou",
    "evalSettings.key.obstacle_coverage_threshold.description":
      "Podíl pizzy zakrytý rukou, papírem nebo víkem (segmentace překážek). Od prahu výš je fotka neplatná.",
    "evalSettings.key.obstacle_enforce.label": "Uplatnit kontrolu překážek",
    "evalSettings.key.crust_gap_threshold.label": "Mezera v krustě",
    "evalSettings.key.crust_gap_threshold.description":
      "Největší mezera v detekovaném okraji krusty jako podíl jeho obvodu. Nad prahem je fotka neplatná (překážka).",
    "evalSettings.key.frame_overshoot_threshold.label": "Okraj záběru",
    "evalSettings.key.frame_overshoot_threshold.description":
      "Jak daleko kruh pizzy přesahuje okraj záběru, jako podíl poloměru. Nad prahem je fotka neplatná (není celá pizza).",
    "evalSettings.key.burnt_threshold.label": "Spálená",
    "evalSettings.key.burnt_threshold.description":
      "p(spálená) z modelu krusty. Od prahu výš je pizza NEOK (spálená).",
    "evalSettings.key.raw_threshold.label": "Nedopečená",
    "evalSettings.key.raw_threshold.description":
      "p(nedopečená) z modelu krusty. Od prahu výš je pizza NEOK (nedopečená).",
    "evalSettings.key.plate_waives_saver.label": "Pizza na talíři nepotřebuje pizza saver",
    "evalSettings.key.plate_waives_saver.description":
      "Zapnuto: když model překážek najde pizzu na talíři, chybějící pizza saver není chyba. Vypnuto: pizza saver je povinný vždy.",
    "evalSettings.key.plate_min_share.label": "Podíl talíře",
    "evalSettings.key.plate_min_share.description":
      "Podíl okolí pizzy, který model překážek vidí jako talíř. Od prahu výš se pizza počítá jako na talíři.",
    "evalSettings.key.extra_ingredients_enabled.label": "Kontrolovat suroviny navíc",
    "evalSettings.key.extra_ingredients_enabled.description":
      "Zapnuto: suroviny nalezené nad rámec objednávky se ukážou u surovin a pizza je NEOK. Vypnuto: nekontroluje se.",
    "evalSettings.key.extra_min_confidence.label": "Jistota kusu",
    "evalSettings.key.extra_min_confidence.description":
      "Nalezený kus se počítá až od této jistoty; méně jisté jsou obvyklé omyly detektoru.",
    "evalSettings.key.extra_min_count.label": "Kusů suroviny",
    "evalSettings.key.extra_min_count.description":
      "Kolik jistých kusů jedné suroviny je potřeba, aby se počítala jako surovina navíc. Skutečná porce je víc kusů, omyl jeden dva. Surovina v Katalogu může potřebovat méně (camembert: 2).",

    "evalSettings.metric.blur_p_blurred": "p(rozmazaná)",
    "evalSettings.metric.obstacle_coverage": "zakrytý podíl",
    "evalSettings.metric.crust_gap_ratio": "mezera v krustě",
    "evalSettings.metric.frame_overshoot_ratio": "přesah",
    "evalSettings.metric.burnt_probability": "p(spálená)",
    "evalSettings.metric.raw_probability": "p(nedopečená)",
    "evalSettings.metric.plate_share": "podíl talíře",
    "evalSettings.metric.plate_share.empty":
      "Podíl talíře zaznamenávají jen vyhodnocení s modelem překážek v7, histogram se tedy plní s novými vyhodnoceními.",

    "evalSettings.card.overridden": "Mimo výchozí",
    "evalSettings.card.unsaved": "Neuloženo",
    "evalSettings.card.pieces": "ks",
    "evalSettings.card.default": "Výchozí {value}",
    "evalSettings.card.isDefault": "Výchozí hodnota",
    "evalSettings.card.reset": "Vrátit výchozí",
    "evalSettings.card.undo": "Vrátit úpravu",
    "evalSettings.card.input": "{label} v %",
    "evalSettings.card.saved": "uloženo {value}",
    "evalSettings.enforce.label": "Uplatnit",
    "evalSettings.enforce.on": "Uplatňuje se",
    "evalSettings.enforce.onHint": "Fotky nad prahem kontrolou neprojdou.",
    "evalSettings.enforce.off": "Stínový režim",
    "evalSettings.enforce.offHint": "Práh jen zaznamená would_fail; verdikt to neovlivní.",
    "evalSettings.requires.off": "Nemá vliv, dokud je „{flag}“ vypnuté.",
    "evalSettings.flag.on": "Zapnuto",
    "evalSettings.flag.off": "Vypnuto",

    "evalSettings.chart.title": "Rozložení",
    "evalSettings.chart.scale": "Měřítko osy Y",
    "evalSettings.chart.linear": "Lin",
    "evalSettings.chart.log": "Log",
    "evalSettings.chart.linearLong": "Lineární počet",
    "evalSettings.chart.logLong": "Logaritmický počet (ukáže chvost)",
    "evalSettings.chart.passes": "Projde",
    "evalSettings.chart.shadow": "{outcome} (stínově)",
    "evalSettings.chart.threshold": "Práh",
    "evalSettings.chart.saved": "Uloženo",
    "evalSettings.chart.empty": "V tomto období žádné hodnoty.",
    "evalSettings.chart.noMetric": "Toto nastavení histogram nemá.",
    "evalSettings.chart.missing": "Backend neposlal histogram pro {metric}.",
    "evalSettings.chart.share": "{share} z {n}",
    "evalSettings.chart.label": "Histogram {metric}",

    "evalSettings.stats.current": "{outcome} při {rule}",
    "evalSettings.stats.vsSaved": "Změna proti uloženému {rule}",
    "evalSettings.stats.pp": "{value} p. b.",
    "evalSettings.stats.noChange": "beze změny",
    "evalSettings.stats.quantiles": "Medián · p90 · p99",
    "evalSettings.stats.zero": "Přesně 0: {count} ({share})",
    "evalSettings.stats.versions": "Verze modelu: {list}",

    "evalSettings.save.pending.one": "{count} neuložená změna",
    "evalSettings.save.pending.few": "{count} neuložené změny",
    "evalSettings.save.pending.other": "{count} neuložených změn",
    "evalSettings.save.hint": "Platí pro nová vyhodnocení v produkci.",
    "evalSettings.save.discard": "Zahodit změny",
    "evalSettings.save.review": "Uložit…",
    "evalSettings.confirm.title": "Uložit do produkce?",
    "evalSettings.confirm.description":
      "Nová vyhodnocení na všech instancích backendu tyto hodnoty použijí zhruba do 10 s. Už vyhodnocené pizzy se znovu nevyhodnocují.",
    "evalSettings.confirm.default": "výchozí",
    "evalSettings.confirm.impact": "{window}: {from} → {to} ({delta})",
    "evalSettings.confirm.save": "Uložit do produkce",
    "evalSettings.confirm.invalid": "Backend odmítl {key}: {error}",
    "evalSettings.confirm.failed": "Uložení selhalo: {error}",
    "evalSettings.toast.saved": "Uloženo jako verze {version}. Nová vyhodnocení ji použijí zhruba do 10 s.",
    "evalSettings.toast.conflict":
      "Mezitím někdo jiný uložil verzi {version}. Aktuální hodnoty jsou znovu načtené a vaše úpravy zůstaly: zkontrolujte je a uložte znovu.",
    "evalSettings.toast.conflictTitle": "Nastavení se mezitím změnilo",

    "evalSettings.history.title": "Historie změn",
    "evalSettings.history.description": "Uložené změny, nejnovější nahoře.",
    "evalSettings.history.empty": "Zatím žádné změny: všechny hodnoty jsou výchozí.",
    "evalSettings.history.version": "v{version}",
    "evalSettings.history.reset": "zpět na výchozí",
    "evalSettings.history.showAll": "Zobrazit vše ({count})",
    "evalSettings.history.showLess": "Zobrazit méně",
  },
});
